/**
 * Vista admin de batches PAC: generar nómina, descargar, subir rendición,
 * marcar como fallido si el flujo se atasca.
 */

import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

type BatchStatus = 'draft' | 'generating' | 'ready' | 'downloaded' | 'settled' | 'failed';

interface Batch {
  id: string;
  scheduledForDate: string;
  status: BatchStatus;
  s3Key: string | null;
  downloadedAt: string | null;
  totalAmount: string | null;
  totalItems: number | null;
  bciFolio: string | null;
  originalBatchId: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface BatchList {
  data: Batch[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 20;
const REFRESH_INTERVAL_MS = 8_000;

const STATUS_LABEL: Record<BatchStatus, { label: string; className: string }> = {
  draft: { label: 'Borrador', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  generating: { label: 'Generando…', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  ready: { label: 'Listo para descargar', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  downloaded: { label: 'Descargado (subir a BCI)', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  settled: { label: 'Liquidado', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  failed: { label: 'Falló', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

const fmtDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtMoney = (raw: string | null) => {
  if (raw === null) return '—';
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
};

const tomorrowDateString = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function PacBatchesPage() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<BatchList | null>(null);
  const [loading, setLoading] = useState(true);
  const [genDate, setGenDate] = useState(tomorrowDateString());
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      const url = `${getAdminEndpointUrl('pacBatches', 'list')}?${params.toString()}`;
      const result = await adminHttpClient.get<BatchList>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando batches:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!data) return;
    const anyInFlight = data.data.some((b) => b.status === 'generating');
    if (!anyInFlight) return;
    const id = setInterval(() => void fetchList(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [data, fetchList]);

  const generateBatch = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await adminHttpClient.post(getAdminEndpointUrl('pacBatches', 'generate'), { scheduledForDate: genDate });
      void fetchList();
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Error generando batch');
    } finally {
      setGenerating(false);
    }
  };

  const downloadBatch = async (batch: Batch) => {
    if (!confirm(`Vas a descargar la nómina BCI del ${batch.scheduledForDate}.\n\nLuego de descargarlo, debes subirlo al portal BCI para que el cobro se ejecute. ¿Continuar?`)) return;
    setActionInProgress(batch.id);
    setActionError(null);
    try {
      const url = getAdminEndpointUrl('pacBatches', 'downloadUrl').replace('{id}', batch.id);
      const res = await adminHttpClient.get<{ url: string }>(url);
      window.open(res.url, '_blank', 'noopener,noreferrer');
      void fetchList();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error descargando');
    } finally {
      setActionInProgress(null);
    }
  };

  const uploadRendition = async (batchId: string, file: File) => {
    setActionInProgress(batchId);
    setActionError(null);
    try {
      const signedRes = await adminHttpClient.post<{ uploadUuid: string; endpoint: string }>(getAdminEndpointUrl('pacBatches', 'renditionUploadUrl'), {});
      const uploadRes = await fetch(signedRes.endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'text/plain' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Error ${uploadRes.status} subiendo archivo`);

      const url = getAdminEndpointUrl('pacBatches', 'renditionRegister').replace('{id}', batchId);
      await adminHttpClient.post(url, { uploadUuid: signedRes.uploadUuid });
      void fetchList();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error subiendo rendición');
    } finally {
      setActionInProgress(null);
    }
  };

  const markFailed = async (batch: Batch) => {
    const reason = prompt(`Marcar batch ${batch.scheduledForDate} como fallido. Razón:`);
    if (!reason) return;
    setActionInProgress(batch.id);
    setActionError(null);
    try {
      const url = getAdminEndpointUrl('pacBatches', 'markFailed').replace('{id}', batch.id);
      await adminHttpClient.post(url, { reason });
      void fetchList();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error marcando fallido');
    } finally {
      setActionInProgress(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Batches PAC (nóminas BCI)</h1>
          <p className="mt-1 text-sm text-slate-500">
            Genera la nómina del día siguiente, descárgala, súbela al portal BCI, y luego sube la rendición que BCI te devuelve. Sólo puede haber un batch en curso a la vez.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-base font-medium">Generar nuevo batch</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">Fecha programada de cobro</label>
              <input type="date" value={genDate} onChange={(e) => setGenDate(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800" />
            </div>
            <button
              type="button"
              disabled={generating}
              onClick={() => void generateBatch()}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? 'Generando…' : 'Generar nómina'}
            </button>
          </div>
          {genError && <p className="mt-2 text-xs text-red-600">{genError}</p>}
        </div>

        {actionError && <p className="text-sm text-red-600">{actionError}</p>}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
              <tr>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Monto</th>
                <th className="px-4 py-3">Folio BCI</th>
                <th className="px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Cargando…
                  </td>
                </tr>
              ) : !data || data.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Sin batches generados.
                  </td>
                </tr>
              ) : (
                data.data.map((b) => {
                  const cfg = STATUS_LABEL[b.status];
                  const inProgress = actionInProgress === b.id;
                  return (
                    <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">{b.scheduledForDate}</p>
                        <p className="text-[11px] text-slate-500">Creado {fmtDateTime(b.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cfg.className}`}>{cfg.label}</span>
                        {b.errorMessage && <p className="mt-1 max-w-xs text-[11px] text-red-600">{b.errorMessage}</p>}
                      </td>
                      <td className="px-4 py-3 text-xs">{b.totalItems ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">{fmtMoney(b.totalAmount)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.bciFolio ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {(b.status === 'ready' || b.status === 'downloaded') && (
                            <button
                              type="button"
                              disabled={inProgress}
                              onClick={() => void downloadBatch(b)}
                              className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-300"
                            >
                              Descargar
                            </button>
                          )}
                          {b.status === 'downloaded' && (
                            <label className="cursor-pointer rounded border border-purple-200 bg-purple-50 px-2 py-1 text-xs text-purple-700 hover:bg-purple-100 dark:border-purple-900 dark:bg-purple-900/20 dark:text-purple-300">
                              Subir rendición
                              <input
                                type="file"
                                accept=".txt,text/plain"
                                className="hidden"
                                disabled={inProgress}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) void uploadRendition(b.id, f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                          {(b.status === 'ready' || b.status === 'downloaded' || b.status === 'generating') && (
                            <button
                              type="button"
                              disabled={inProgress}
                              onClick={() => void markFailed(b)}
                              className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
                            >
                              Marcar fallido
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {data && data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs dark:border-slate-800">
              <span className="text-slate-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Anterior
                </button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
